import React, { useCallback, useEffect } from 'react';
import { Ellipsis } from 'react-spinners-css';
import { Container, Title, List, Header, ScrollControl } from './styles';
import { SearchBar } from '../../search_bar';
import { Scroll } from '../../layout_components/scroll';
import { Item } from './item';
import { ModalConfirm } from '../modal_confirm';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../infrastructure/redux/store';

interface FormListItem<T> {
  id: number;
  label: string;
  data: T;
  middle_component?: React.ReactNode;
}

interface Props<T> {
  list_name: string;
  items?: FormListItem<T>[];
  loading?: boolean;
  onScroll?: () => void;
  onSearch: (value: string) => void;
  onEdit?: (data: T) => void;
  onDelete?: (id: number) => void;
}

const FormList = <T,>(props: Props<T>) => {
  const { theme } = useSelector((state: RootState) => state.theme);
  const [delete_item, setDeleteItem] = React.useState<FormListItem<T> | undefined>();

  const handle_delete = useCallback(
    (id: number) => {
      if (props.onDelete) {
        props.onDelete(id);
        setDeleteItem(undefined);
      }
    },
    [props.onDelete]
  );

  // implementação do infinite scroll
  useEffect(() => {
    const intersection_observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        if (props.onScroll) {
          props.onScroll();
        }
      }
    });

    const scroll_control = document.getElementById('scroll-control');

    if (scroll_control) {
      intersection_observer.observe(scroll_control);
    }

    return () => intersection_observer.disconnect();
  }, []);

  return (
    <>
      <Container>
        <Header>
          <Title>{props.list_name}</Title>
          <SearchBar
            onSearch={props.onSearch}
            placeholder={`Filtrar ${props.list_name.toLowerCase()}...`}
          />
        </Header>

        <Scroll>
          <List>
            {props.items?.map((item) => (
              <Item
                key={item.id}
                label={item.label}
                onEdit={() => props.onEdit && props.onEdit(item.data)}
                onDelete={() => setDeleteItem(item)}
                loading={props.loading}
              >
                {item.middle_component}
              </Item>
            ))}

            <ScrollControl id='scroll-control'>
              <Ellipsis size={40} color={theme.colors.textBackground} />
            </ScrollControl>
          </List>
        </Scroll>
      </Container>

      {delete_item && (
        <ModalConfirm
          title='Tem certeza que deseja deletar?'
          message={delete_item.label}
          onConfirm={() => handle_delete(delete_item.id)}
          onCancel={() => setDeleteItem(undefined)}
        />
      )}
    </>
  );
};

export { FormList };
export type { FormListItem };
